import { useMemo } from "react";
import { COUNTRY_CODES, SPAIN_CODE } from "@tc/shared";
import { formatCountry, type Locale } from "@tc/i18n";
import { Select } from "./select";

export interface CountrySelectProps {
  value?: string;
  onChange?: (value: string) => void;
  /** Active UI locale — country names are localized and sorted in it. */
  locale: Locale;
  /** When provided, prepends an empty option with this label (for an optional, clearable field). */
  emptyLabel?: string;
}

/**
 * A country picker over the ISO 3166-1 alpha-2 codes ({@link COUNTRY_CODES}). Names are localized
 * via `@tc/i18n`'s {@link formatCountry}, Spain is pinned to the top (TC's home market) and the
 * rest sorted by localized name. The stored value is always the 2-letter code; a value that isn't
 * a known code (legacy free text) is preserved as its own trailing option so it's never silently
 * lost.
 */
export function CountrySelect({ value, onChange, locale, emptyLabel }: CountrySelectProps) {
  const options = useMemo(() => {
    const collator = new Intl.Collator(locale);
    const rest = COUNTRY_CODES.filter((c) => c !== SPAIN_CODE)
      .map((c) => ({ value: c, label: formatCountry(locale, c) }))
      .sort((a, b) => collator.compare(a.label, b.label));
    const opts = [{ value: SPAIN_CODE, label: formatCountry(locale, SPAIN_CODE) }, ...rest];
    if (emptyLabel !== undefined) opts.unshift({ value: "", label: emptyLabel });
    if (value && !COUNTRY_CODES.includes(value as (typeof COUNTRY_CODES)[number])) {
      opts.push({ value, label: value });
    }
    return opts;
  }, [locale, emptyLabel, value]);

  return <Select value={value} onChange={onChange} options={options} />;
}
